import { TriangleAlert, X } from 'lucide-react';
import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './button';


interface ConfirmationModalProps {
  open: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  requireTextMatch?: {
    text: string;
    label: string;
  };
  inputValue?: string;
  onInputChange?: (value: string) => void;
  hideConfirmButton?: boolean;
  children?: React.ReactNode;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  title,
  message,
  confirmText,
  cancelText,
  variant = 'danger',
  onConfirm,
  onCancel,
  loading = false,
  requireTextMatch,
  inputValue = '',
  onInputChange,
  hideConfirmButton = false,
  children,
}) => {
  const { t } = useTranslation();
  if (!open) return null;

  const resolvedConfirmText = confirmText ?? t('global.confirm');
  const resolvedCancelText = cancelText ?? t('global.cancel');

  const isConfirmDisabled = loading || (requireTextMatch && inputValue !== requireTextMatch.text);

  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
        return {
          iconColor: 'text-red-400',
          confirmButtonVariant: 'destructive' as const,
          icon: TriangleAlert,
        };
      case 'warning':
        return {
          iconColor: 'text-yellow-400',
          confirmButtonVariant: 'default' as const,
          icon: TriangleAlert,
        };
      case 'info':
        return {
          iconColor: 'text-blue-400',
          confirmButtonVariant: 'default' as const,
          icon: TriangleAlert,
        };
      default:
        return {
          iconColor: 'text-red-400',
          confirmButtonVariant: 'destructive' as const,
          icon: TriangleAlert,
        };
    }
  };

  const { iconColor, confirmButtonVariant, icon: Icon } = getVariantStyles();

  // Rendered at the end of <body>: inside a page, the parent's spacing (e.g. space-y-*)
  // would add a margin to this fixed overlay and leave a strip uncovered at the top.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative rounded-xl border border-border bg-card w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <button
            onClick={onCancel}
            className="text-muted-foreground hover:text-foreground"
            disabled={loading}
            aria-label={t('global.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Icon className={`h-6 w-6 ${iconColor}`} />
            </div>
            <div className="flex-1">
              <div className="whitespace-pre-line text-sm text-foreground">
                {message}
              </div>

              {children && (
                <div className="mt-3">
                  {children}
                </div>
              )}

              {requireTextMatch && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {requireTextMatch.label}
                  </label>
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => onInputChange?.(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                    placeholder={t('common.typeToConfirm', { text: requireTextMatch.text })}
                    disabled={loading}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border flex justify-end space-x-3">
          <Button
            onClick={onCancel}
            variant="outline"
            disabled={loading}
          >
            {resolvedCancelText}
          </Button>
          {!hideConfirmButton && (
            <Button
              onClick={onConfirm}
              variant={confirmButtonVariant}
              disabled={isConfirmDisabled}
            >
              {loading ? t('common.processing') : resolvedConfirmText}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body

  );
};

export default ConfirmationModal;
